import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FilePen, Mail, Twitter, Github, Linkedin } from "lucide-react";
import ComingSoonModal from "./ComingSoonModal";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [comingSoonModal, setComingSoonModal] = useState<{
    isOpen: boolean;
    title: string;
  }>({ isOpen: false, title: "" });

  const handleComingSoonClick = (title: string) => {
    setComingSoonModal({ isOpen: true, title });
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xl font-semibold">
              <FilePen className="w-5 h-5" />
              <span>CampusQuest</span>
            </div>
            <p className="text-gray-300 leading-relaxed">
              Your comprehensive AI copilot for college applications. From essay
              brainstorming to deadline tracking, get end-to-end guidance
              throughout your entire application journey.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://twitter.com/campusquest"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/essay-pilot/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="mailto:team@essaypilot.io"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Product</h3>
            <div className="space-y-3">
              <Link
                to="/#features"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Features
              </Link>
              <Link
                to="/#pricing-section"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/applicant-dashboard"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                My Essays
              </Link>
              <Link
                to="/#faq-section"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                FAQ
              </Link>
            </div>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Resources</h3>
            <div className="space-y-3">
              <button
                onClick={() =>
                  window.open(
                    "https://jewel-casquette-19c.notion.site/How-to-Write-a-Personal-Statement-That-Stands-Out-2a948fe23dfb81f98918febd4cd217ee",
                    "_blank"
                  )
                }
                className="block text-gray-300 hover:text-white transition-colors text-left"
              >
                Blog
              </button>
              <button
                onClick={() => handleComingSoonClick("Essay Writing Tips")}
                className="block text-gray-300 hover:text-white transition-colors text-left"
              >
                Essay Writing Tips
              </button>
              <button
                onClick={() => handleComingSoonClick("College Guides")}
                className="block text-gray-300 hover:text-white transition-colors text-left"
              >
                College Guides
              </button>
              <button
                onClick={() => handleComingSoonClick("Success Stories")}
                className="block text-gray-300 hover:text-white transition-colors text-left"
              >
                Success Stories
              </button>
            </div>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Support</h3>
            <div className="space-y-3">
              <a
                href="mailto:support@campusquest.io"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Help Center
              </a>
              <a
                href="mailto:team@essaypilot.io"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Contact Us
              </a>
              <Link
                to="/privacy"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} CampusQuest. All rights reserved.
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <span>Made with ❤️ for students</span>
              <div className="flex items-center space-x-4">
                <Link
                  to="/privacy"
                  className="hover:text-white transition-colors"
                >
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  className="hover:text-white transition-colors"
                >
                  Terms
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="text-center">
            <p className="text-gray-400 text-sm leading-relaxed max-w-3xl mx-auto">
              CampusQuest is an independent AI copilot service designed to guide
              students through their entire college application process. We are
              not affiliated with any college, university, or admissions
              committee. Our AI provides comprehensive guidance and suggestions,
              but final decisions about your application remain yours.
            </p>
          </div>
        </div>
      </div>

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, title: "" })}
        title={comingSoonModal.title}
      />
    </footer>
  );
};

export default Footer;
